import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import StatusBadge from './StatusBadge';

const AssetDetailDrawer = ({ asset, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  if (!asset || !isOpen) return null;

  const ownerOptions = [
    { value: 'Arjun Sharma', label: 'Arjun Sharma' },
    { value: 'Priya Patel', label: 'Priya Patel' },
    { value: 'Rahul Verma', label: 'Rahul Verma' },
    { value: 'Anjali Singh', label: 'Anjali Singh' },
    { value: 'Vikram Nair', label: 'Vikram Nair' },
    { value: 'Neha Gupta', label: 'Neha Gupta' },
    { value: 'Suresh Reddy', label: 'Suresh Reddy' },
    { value: 'Kavita Joshi', label: 'Kavita Joshi' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'spare', label: 'Spare' },
    { value: 'repair', label: 'Repair' },
    { value: 'retired', label: 'Retired' }
  ];

  const assetHistory = [
    {
      id: 1,
      action: 'Asset Created',
      user: 'System Admin',
      timestamp: new Date(asset.purchaseDate),
      details: 'Initial asset registration'
    },
    {
      id: 2,
      action: 'Owner Assigned',
      user: 'IT Administrator',
      timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      details: `Assigned to ${asset?.owner}`
    },
    {
      id: 3,
      action: 'Location Updated',
      user: asset?.owner,
      timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      details: 'Moved to Room 205'
    },
    {
      id: 4,
      action: 'Status Changed',
      user: 'IT Administrator',
      timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      details: `Status updated to ${asset?.status}`
    }
  ];

  const handleStatusUpdate = (newStatus) => {
    onUpdate({ ...asset, status: newStatus });
    console.log('Status updated to:', newStatus);
  };

  const handleOwnerChange = (newOwner) => {
    onUpdate({ ...asset, owner: newOwner });
    console.log('Owner changed to:', newOwner);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 lg:relative lg:inset-auto">
      {/* Mobile Overlay */}
      <div 
        className="fixed inset-0 bg-black/20 lg:hidden" 
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-card border-l border-border shadow-elevated lg:relative lg:w-96 lg:max-h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">Asset Details</h2>
              <p className="text-sm text-muted-foreground font-mono">{asset?.assetId}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Barcode Display */}
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <div className="mb-3">
              <div className="inline-block bg-white p-3 rounded border">
                <div className="font-mono text-xs space-y-1">
                  <div className="h-8 bg-black" style={{
                    background: 'repeating-linear-gradient(90deg, black 0px, black 2px, white 2px, white 4px)'
                  }}></div>
                  <div className="text-center text-black font-bold">{asset?.assetId}</div>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Scan for quick identification</p>
          </div>

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-card-foreground uppercase tracking-wide">
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Asset Type
                </label>
                <p className="text-sm font-medium text-card-foreground mt-1 capitalize">
                  {asset?.type}
                </p>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Current Owner
                </label>
                <div className="mt-1">
                  <Select
                    options={ownerOptions}
                    value={asset?.owner}
                    onChange={handleOwnerChange}
                    placeholder="Select owner..."
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Purchase Date
                </label>
                <p className="text-sm text-card-foreground mt-1">
                  {formatDate(asset?.purchaseDate)}
                </p>
              </div>
              
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Warranty Expiration
                </label>
                <p className="text-sm text-card-foreground mt-1">
                  {formatDate(asset?.warrantyExpiration)}
                </p>
              </div>
            </div>
          </div>

          {/* Status Management */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-card-foreground uppercase tracking-wide">
              Status Management
            </h3>
            
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Current Status
                </label>
                <div className="mt-1">
                  <StatusBadge status={asset?.status} />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {statusOptions?.map((status) => (
                <Button
                  key={status?.value}
                  variant={asset?.status === status?.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusUpdate(status?.value)}
                  className="text-xs"
                >
                  {status?.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Purchase Documentation */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-card-foreground uppercase tracking-wide">
              Documentation
            </h3>
            
            <div className="border border-border rounded-lg p-4 bg-muted/30">
              <div className="flex items-center space-x-3">
                <Icon name="FileText" size={20} className="text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-card-foreground">Purchase Receipt</p>
                  <p className="text-xs text-muted-foreground">PDF • 2.4 MB</p>
                </div>
                <Button variant="ghost" size="icon">
                  <Icon name="Download" size={16} />
                </Button>
              </div>
            </div>
            
            <div className="border border-border rounded-lg p-4 bg-muted/30">
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={20} className="text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-card-foreground">Warranty Certificate</p>
                  <p className="text-xs text-muted-foreground">PDF • 1.8 MB</p>
                </div>
                <Button variant="ghost" size="icon">
                  <Icon name="Download" size={16} />
                </Button>
              </div>
            </div>
          </div>

          {/* Asset History Timeline */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-card-foreground uppercase tracking-wide">
              Asset History
            </h3>
            
            <div className="space-y-4">
              {assetHistory?.map((event, index) => (
                <div key={event?.id} className="flex space-x-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    {index < assetHistory?.length - 1 && (
                      <div className="w-px h-8 bg-border mt-2"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-card-foreground">
                        {event?.action}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(event?.timestamp)}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      by {event?.user}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {event?.details}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border p-6">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              fullWidth
            >
              Edit Details
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="MapPin"
              iconPosition="left"
              fullWidth
            >
              Update Location
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailDrawer;